import { faEraser, faFilter } from '@fortawesome/free-solid-svg-icons';
import { Autocomplete, Popover, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useLocale } from 'hooks';
import { useI18n } from 'locales';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';

import { Button } from 'components/button';
import { DatePicker, DateTimePicker } from 'components/input';

function FilterMenu({ model, filters, filtersInfo, onChangeFilter, onClearFilter, disabledFilters = [] }) {
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);
  const t = useI18n();
  const locale = useLocale();

  const hasFilters = filtersInfo && filtersInfo.data &&
    !Array.isArray(filtersInfo.data);

  // Convert camelCase filterKey to readable label
  const getFilterLabel = useCallback((filterKey) => {
    const keyWithoutId = filterKey.endsWith('Id') ? filterKey.slice(0, -2) : filterKey;

    return keyWithoutId
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }, []);

  const handleFilterMenuOpen = useCallback((event) => {
    setFilterAnchorEl(event.currentTarget);
  }, []);

  const handleFilterMenuClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  const renderFilterInput = useCallback((filterKey) => {
    const filterOptions = filtersInfo?.data?.[filterKey];
    const isDisabled = disabledFilters.includes(filterKey);

    if (!filterOptions || !Array.isArray(filterOptions) || filterOptions.length === 0 || isDisabled) {
      return null;
    }

    return (
      <Autocomplete
        key={`filter-${filterKey}`}
        autoHighlight
        disabled={isDisabled}
        options={filterOptions}
        renderInput={(params) => <TextField {...params} label={t(getFilterLabel(filterKey))} />}
        getOptionKey={(option) => option.value}
        getOptionLabel={(option) => {
          if (locale === 'zh-Hant' && option.labelChi) {
            return option.labelChi;
          } else if (locale === 'zh-Hans' && option.labelSimplifiedChi) {
            return option.labelSimplifiedChi;
          } else {
            return option.label || '';
          }
        }}
        value={filterOptions.find((option) => option.value === filters.get(filterKey)) || null}
        onChange={(e, newValue) => onChangeFilter(filterKey, newValue ? newValue.value : null)}
        sx={{ flexGrow: 1 }}
      />
    );
  }, [filters, filtersInfo, onChangeFilter, getFilterLabel, disabledFilters, t]);

  if (!hasFilters) {
    return null;
  }

  return (
    <>
      <Button
        size='small'
        label={t('Filter')}
        leading={faFilter}
        variant='outlined'
        onClick={handleFilterMenuOpen}
      />
      <Popover
        anchorEl={filterAnchorEl}
        open={filterMenuOpen}
        onClose={handleFilterMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '250px',
              width: '90vw',
              maxWidth: '400px',
              padding: 2,
              boxShadow: 10,
              zIndex: 1000,
            }
          }
        }}
      >
        <Stack spacing={2}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            {t('Filter Options')}
          </Typography>

          {Object.keys(filtersInfo.data || {}).map(renderFilterInput)}

          {
            model === 'Receipt' ? (
              <>
                <DatePicker
                  label={t('Date (From)')}
                  value={filters.get('dateFrom') || null}
                  onAccept={(newValue) => onChangeFilter('dateFrom', newValue ?? null)}
                  disableFuture={true}
                  clearable={true}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () => onChangeFilter('dateFrom', null)
                    }
                  }}
                />
                <DatePicker
                  label={t('Date (To)')}
                  value={filters.get('dateTo') || null}
                  onAccept={(newValue) => onChangeFilter('dateTo', newValue ?? null)}
                  disableFuture={true}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () => onChangeFilter('dateTo', null)
                    }
                  }}
                />
              </>
            ) : (
              <>
                <DateTimePicker
                  label={t('Date (From)')}
                  value={filters.get('dateFrom') || null}
                  onAccept={(newValue) => onChangeFilter('dateFrom', newValue ?? null)}
                  disableFuture={true}
                  clearable
                  onClear={() => onChangeFilter('dateFrom', null)}
                />
                <DateTimePicker
                  label={t('Date (To)')}
                  value={filters.get('dateTo') || null}
                  onAccept={(newValue) => onChangeFilter('dateTo', newValue ?? null)}
                  disableFuture={true}
                  clearable
                  onClear={() => onChangeFilter('dateTo', null)}
                />
              </>
            )
          }
          <Button
            leading={faEraser}
            variant='outlined'
            onClick={() => {
              handleFilterMenuClose();
              onClearFilter();
            }}>
            {t('Clear Filters')}
          </Button>
        </Stack>
      </Popover>
    </>
  );
}

FilterMenu.propTypes = {
  model: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  filtersInfo: PropTypes.object,
  onChangeFilter: PropTypes.func.isRequired,
  disabledFilters: PropTypes.arrayOf(PropTypes.string),
};

export default FilterMenu;
